<template>
  <div class="descriptor-configuration">
    <div class="header-section">
      <h1 class="page-title">Descriptor Setup</h1>
      <p class="page-subtitle">
        Define performance descriptors for competencies and traits
      </p>
    </div>

    <div class="main-content">
      <div class="content-layout">
        <!-- Left Panel - Competency Hierarchy -->
        <div class="left-panel">
          <div class="panel-header">
            <h3 class="panel-title">Select Competency/Trait</h3>
            <p class="panel-hint">
              Choose a competency or trait to configure its descriptors
            </p>
            <div class="search-box">
              <i class="bi bi-search"></i>
              <input type="text" placeholder="Search Hierarchy..." v-model="hierarchySearchQuery" />
            </div>
          </div>

          <div class="hierarchy-tree">
            <div v-for="competency in filteredCompetencies" :key="competency.id" class="tree-node">
              <!-- Root Competency Node -->
              <div class="competency-node" :class="{
                'has-children': hasChildren(competency.id),
                selectable: true,
                active: selectedItem?.id === competency.id && selectedItem?.type === 'competency',
              }" @click="selectItem(competency, 'competency')">
                <div class="node-content">
                  <i v-if="hasChildren(competency.id)" class="chevron-icon bi"
                    :class="isExpanded(competency.id) ? 'bi-chevron-down' : 'bi-chevron-right'"
                    @click.stop="toggleExpand(competency.id)"></i>
                  <i class="competency-type-icon bi bi-folder-fill"></i>
                  <span class="competency-name">{{ competency.name }}</span>
                  <div class="status-indicators">
                    <i v-if="competency.descriptorStatus === false" class="status-icon bi bi-lock-fill"
                      title="Descriptors locked"></i>
                    <i v-else-if="competency.descriptorStatus === true" class="status-icon bi bi-unlock-fill"
                      title="Can add descriptors"></i>
                  </div>
                </div>
                <div class="competency-meta">
                  <span class="items-count">{{ getChildrenCount(competency.id) }} sub-competencies</span>
                  <span class="descriptor-count">
                    {{ getDescriptorCount(competency.id, 'competency') }} descriptors
                  </span>
                </div>
              </div>

              <!-- Traits under Root Competency -->
              <div v-if="getTraitsForCompetency(competency.id).length"
                class="traits-container">
                <div v-for="trait in getTraitsForCompetency(competency.id)" :key="trait.id" class="trait-wrapper">
                  <div class="trait-node selectable" :class="{
                    active: selectedItem?.id === trait.id && selectedItem?.type === 'trait',
                  }" @click="selectItem(trait, 'trait')">
                    <div class="trait-content">
                      <div class="node-header">
                        <div class="tree-lines">
                          <div class="vertical-line"></div>
                          <div class="horizontal-line"></div>
                        </div>
                        <i class="trait-type-icon bi bi-bookmark-fill"></i>
                        <span class="trait-name">{{ trait.name }}</span>
                        <div class="status-indicators">
                          <i v-if="trait.descriptorStatus === false" class="status-icon bi bi-lock-fill"
                            title="Descriptors locked"></i>
                          <i v-else-if="trait.descriptorStatus === true" class="status-icon bi bi-unlock-fill"
                            title="Can add descriptors"></i>
                        </div>
                      </div>
                      <div class="trait-meta">
                        <span class="descriptor-count">
                          {{ getDescriptorCount(trait.id, 'trait') }} descriptors
                        </span>
                        <span class="status-pill" :class="getDescriptorStatusClass(trait.id, 'trait')">
                          {{ getDescriptorStatusText(trait.id, 'trait') }}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <!-- Sub-Competencies -->
              <div v-if="isExpanded(competency.id)" class="children-container">
                <div v-for="subComp in getChildren(competency.id)" :key="subComp.id" class="child-wrapper">
                  <div class="sub-competency-node" :class="{
                    'has-children': hasChildren(subComp.id),
                    selectable: true,
                    active: selectedItem?.id === subComp.id && selectedItem?.type === 'competency',
                  }" @click="selectItem(subComp, 'competency')">
                    <div class="child-content">
                      <div class="node-header">
                        <div class="tree-lines">
                          <div class="vertical-line"></div>
                          <div class="horizontal-line"></div>
                        </div>
                        <i v-if="hasChildren(subComp.id)" class="chevron-icon bi"
                          :class="isExpanded(subComp.id) ? 'bi-chevron-down' : 'bi-chevron-right'"
                          @click.stop="toggleExpand(subComp.id)"></i>
                        <i class="competency-type-icon bi bi-folder2-open"></i>
                        <span class="child-name">{{ subComp.name }}</span>
                        <div class="status-indicators">
                          <i v-if="subComp.descriptorStatus === false" class="status-icon bi bi-lock-fill"
                            title="Descriptors locked"></i>
                          <i v-else-if="subComp.descriptorStatus === true" class="status-icon bi bi-unlock-fill"
                            title="Can add descriptors"></i>
                        </div>
                      </div>
                      <div class="child-meta">
                        <span class="items-count">{{ getChildrenCount(subComp.id) }} children</span>
                        <span class="descriptor-count">
                          {{ getDescriptorCount(subComp.id, 'competency') }} descriptors
                        </span>
                      </div>
                    </div>
                  </div>

                  <!-- Traits under Sub-Competency -->
                  <div v-if="getTraitsForCompetency(subComp.id).length"
                    class="traits-container">
                    <div v-for="trait in getTraitsForCompetency(subComp.id)" :key="trait.id" class="trait-wrapper">
                      <div class="trait-node selectable" :class="{
                        active: selectedItem?.id === trait.id && selectedItem?.type === 'trait',
                      }" @click="selectItem(trait, 'trait')">
                        <div class="trait-content">
                          <div class="node-header">
                            <div class="tree-lines">
                              <div class="vertical-line"></div>
                              <div class="horizontal-line"></div>
                            </div>
                            <i class="trait-type-icon bi bi-bookmark-fill"></i>
                            <span class="trait-name">{{ trait.name }}</span>
                            <div class="status-indicators">
                              <i v-if="trait.descriptorStatus === false" class="status-icon bi bi-lock-fill"
                                title="Descriptors locked"></i>
                              <i v-else-if="trait.descriptorStatus === true" class="status-icon bi bi-unlock-fill"
                                title="Can add descriptors"></i>
                            </div>
                          </div>
                          <div class="trait-meta">
                            <span class="descriptor-count">
                              {{ getDescriptorCount(trait.id, 'trait') }} descriptors
                            </span>
                            <span class="status-pill" :class="getDescriptorStatusClass(trait.id, 'trait')">
                              {{ getDescriptorStatusText(trait.id, 'trait') }}
                            </span>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  <!-- Grandchildren -->
                  <div v-if="isExpanded(subComp.id)" class="grandchildren-container">
                    <div v-for="childComp in getChildren(subComp.id)" :key="childComp.id" class="grandchild-wrapper">
                      <div class="child-competency-node" :class="{
                        'has-children': hasTraits(childComp.id),
                        selectable: true,
                        active: selectedItem?.id === childComp.id && selectedItem?.type === 'competency',
                      }" @click="selectItem(childComp, 'competency')">
                        <div class="grandchild-content">
                          <div class="node-header">
                            <div class="tree-lines">
                              <div class="vertical-line"></div>
                              <div class="horizontal-line"></div>
                            </div>
                            <i v-if="hasTraits(childComp.id)" class="chevron-icon bi"
                              :class="isExpanded(`comp-${childComp.id}`) ? 'bi-chevron-down' : 'bi-chevron-right'"
                              @click.stop="toggleExpand(`comp-${childComp.id}`)"></i>
                            <i class="competency-type-icon bi bi-folder"></i>
                            <span class="grandchild-name">{{ childComp.name }}</span>
                            <div class="status-indicators">
                              <i v-if="childComp.descriptorStatus === false" class="status-icon bi bi-lock-fill"
                                title="Descriptors locked"></i>
                              <i v-else-if="childComp.descriptorStatus === true" class="status-icon bi bi-unlock-fill"
                                title="Can add descriptors"></i>
                            </div>
                          </div>
                          <div class="grandchild-meta">
                            <span class="items-count">{{ getTraitsForCompetency(childComp.id).length }} traits</span>
                            <span class="descriptor-count">
                              {{ getDescriptorCount(childComp.id, 'competency') }} descriptors
                            </span>
                          </div>
                        </div>
                      </div>

                      <!-- Traits under Grandchild Competency -->
                      <div v-if="getTraitsForCompetency(childComp.id).length"
                        class="traits-container">
                        <div v-for="trait in getTraitsForCompetency(childComp.id)" :key="trait.id"
                          class="trait-wrapper">
                          <div class="trait-node selectable" :class="{
                            active: selectedItem?.id === trait.id && selectedItem?.type === 'trait',
                          }" @click="selectItem(trait, 'trait')">
                            <div class="trait-content">
                              <div class="node-header">
                                <div class="tree-lines">
                                  <div class="vertical-line"></div>
                                  <div class="horizontal-line"></div>
                                </div>
                                <i class="trait-type-icon bi bi-bookmark-fill"></i>
                                <span class="trait-name">{{ trait.name }}</span>
                                <div class="status-indicators">
                                  <i v-if="trait.descriptorStatus === false" class="status-icon bi bi-lock-fill"
                                    title="Descriptors locked"></i>
                                  <i v-else-if="trait.descriptorStatus === true" class="status-icon bi bi-unlock-fill"
                                    title="Can add descriptors"></i>
                                </div>
                              </div>
                              <div class="trait-meta">
                                <span class="descriptor-count">
                                  {{ getDescriptorCount(trait.id, 'trait') }} descriptors
                                </span>
                                <span class="status-pill" :class="getDescriptorStatusClass(trait.id, 'trait')">
                                  {{ getDescriptorStatusText(trait.id, 'trait') }}
                                </span>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                      <!-- End Traits under Grandchild -->
                    </div>
                  </div>
                  <!-- End Grandchildren -->
                </div>
              </div>
            </div>
          </div>


        </div>

        <!-- Right Panel - Descriptors -->
        <div class="right-panel">
          <div v-if="!selectedItem" class="empty-state">
            <div class="empty-icon"><i class="bi bi-card-text"></i></div>
            <h3>Select a Competency or Trait</h3>
            <p>
              Choose a competency or trait to manage its performance descriptors
            </p>
            <div class="selection-hint">
              <i class="bi bi-info-circle"></i>
              <span>Descriptors define performance levels and criteria</span>
            </div>
          </div>

          <div v-else class="descriptor-section">
            <!-- Descriptor Header -->
            <div class="descriptor-header">
              <div class="header-left">
                <h3 class="descriptor-title">
                  {{ selectedItem.name }} - Performance Descriptors
                </h3>
                <div class="item-info">
                  <span class="item-type-badge" :class="`type-${selectedItem.type}`">
                    {{
                      selectedItem.type === "competency"
                        ? "Competency"
                        : "Trait"
                    }}
                  </span>
                  <span class="descriptor-count">
                    {{ displayedDescriptors.length }} descriptors configured
                  </span>
                </div>
              </div>
              <div class="header-right">
                <div class="type-selector" v-if="canAddDescriptors">
                  <label>Performance Levels:</label>
                  <select v-model="descriptorType" @change="handleTypeChange" class="type-select">
                    <option value="">Select Type</option>
                    <option value="2">2 Levels (Low, High)</option>
                    <option value="3">3 Levels (Low, Medium, High)</option>
                    <option value="4">
                      4 Levels (Low, Medium, High, Excellent)
                    </option>
                    <option value="5">
                      5 Levels (Very Low, Low, Medium, High, Very High)
                    </option>
                  </select>
                </div>
                <button class="add-btn" @click="openAddDescriptorModal" :disabled="!canAddDescriptors" :title="canAddDescriptors
                  ? 'Add Custom Descriptor'
                  : 'Descriptors are locked for this item'
                  ">
                  <i class="bi" :class="canAddDescriptors ? 'bi-plus-circle' : 'bi-lock'"></i>
                  {{ canAddDescriptors ? "Add Custom Descriptor" : "Locked" }}
                </button>
              </div>
            </div>

            <!-- Locked Message -->
            <div v-if="!canAddDescriptors" class="locked-message">
              <div class="locked-content">
                <i class="bi bi-lock-fill"></i>
                <div class="locked-text">
                  <h4>Descriptors Locked</h4>
                  <p>
                    This {{ selectedItem.type }} has descriptorStatus set to
                    false. Descriptors cannot be added or modified.
                  </p>
                </div>
              </div>
            </div>

            <!-- Descriptors Table -->
            <div class="table-container">
              <table class="descriptors-table" v-if="displayedDescriptors.length > 0">
                <thead>
                  <tr>
                    <th class="status-col">Status</th>
                    <th class="range-col">Range</th>
                    <th class="description-col">Description</th>
                    <!-- <th class="mapping-col">Trait Mapping</th> -->
                    <th class="actions-col" v-if="canAddDescriptors">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="(descriptor, index) in displayedDescriptors" :key="descriptor.id" class="descriptor-row"
                    :class="getDescriptorRowClass(descriptor.status)">
                    <td class="status-col">
                      <span class="status-badge" :class="getStatusBadgeClass(descriptor.status)">
                        {{ descriptor.status }}
                      </span>
                    </td>
                    <td class="range-col">
                      <div class="range-display">
                        <span class="range-text">
                          {{ descriptor.min }}% - {{ descriptor.max }}%
                        </span>
                      </div>
                    </td>
                    <td class="description-col">
                      <div class="description-container">
                        <span class="description-text">
                          {{ descriptor.description || "No description" }}
                        </span>
                      </div>
                    </td>
                    <!-- <td class="mapping-col">
                      <div class="mapping-container">
                        <div
                          v-if="selectedItem.type === 'trait'"
                          class="trait-mapping"
                        >
                          <span class="mapping-info">
                            <i class="bi bi-bookmark-fill"></i>
                            Direct trait mapping
                          </span>
                        </div>
                        <div
                          v-else-if="selectedItem.type === 'competency'"
                          class="competency-mapping"
                        >
                          <span class="mapping-count">
                            {{ getLinkedTraitsCount(selectedItem.id) }} linked
                            traits
                          </span>
                          <button
                            class="view-mapping-btn"
                            @click="showMappingDetails(selectedItem.id)"
                            v-if="getLinkedTraitsCount(selectedItem.id) > 0"
                          >
                            View
                          </button>
                        </div>
                      </div>
                    </td> -->
                    <td class="actions-col" v-if="canAddDescriptors">
                      <div class="actions-container">
                        <button class="action-btn edit-btn" @click="editDescriptor(index)" title="Edit">
                          <i class="bi bi-pencil"></i>
                        </button>
                        <button class="action-btn delete-btn" @click="deleteDescriptor(index)" title="Remove">
                          <i class="bi bi-trash"></i>
                        </button>
                      </div>
                    </td>
                  </tr>
                </tbody>
              </table>

              <div v-if="displayedDescriptors.length === 0" class="table-empty">
                <div class="empty-icon"><i class="bi bi-plus-circle"></i></div>
                <h4>No Descriptors Configured</h4>
                <p v-if="canAddDescriptors">
                  Select a performance level type to automatically generate
                  descriptors
                </p>
                <p v-else>
                  This {{ selectedItem.type }} has locked descriptors
                </p>
                <div class="quick-actions" v-if="canAddDescriptors">
                  <button class="quick-btn" @click="setDescriptorType('2')">
                    2 Levels
                  </button>
                  <button class="quick-btn" @click="setDescriptorType('3')">
                    3 Levels
                  </button>
                </div>
              </div>
            </div>

            <!-- Configure Section -->
            <div class="configure-section">
              <button class="configure-btn" @click="configureDescriptors" :disabled="!allItemsHaveDescriptors">
                {{
                  allItemsHaveDescriptors
                    ? "Complete Descriptor Setup"
                    : `Complete All Items (${incompleteItemsCount} remaining)`
                }}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Mapping Details Modal -->
    <div v-if="showMappingModal" class="modal-overlay" @click="closeMappingModal">
      <div class="modal-content mapping-modal" @click.stop>
        <div class="modal-header">
          <h3>Trait Mapping Details</h3>
          <button class="close-btn" @click="closeMappingModal">
            <i class="bi bi-x"></i>
          </button>
        </div>
        <div class="modal-body">
          <div class="mapping-details">
            <h4>{{ mappingDetails.competencyName }}</h4>
            <p class="mapping-description">
              This competency is linked to the following traits:
            </p>
            <div class="traits-list">
              <div v-for="trait in mappingDetails.traits" :key="trait.id" class="trait-item">
                <i class="bi bi-bookmark-fill"></i>
                <span class="trait-name">{{ trait.name }}</span>
                <span class="trait-descriptors">
                  {{ getDescriptorCount(trait.id, "trait") }} descriptors
                </span>
              </div>
            </div>
          </div>
        </div>
        <div class="modal-footer">
          <button class="cancel-btn" @click="closeMappingModal">Close</button>
        </div>
      </div>
    </div>

    <!-- Rest of existing modals (Edit Descriptor, Delete Confirmation) remain the same -->
    <!-- Edit Descriptor Modal -->
    <div v-if="showModal" class="modal-overlay" @click="closeModal">
      <div class="modal-content" @click.stop>
        <div class="modal-header">
          <h3>{{ editingDescriptor ? "Edit" : "Add" }} Descriptor</h3>
          <button class="close-btn" @click="closeModal">
            <i class="bi bi-x"></i>
          </button>
        </div>
        <div class="modal-body">
          <div class="form-group">
            <label>Status *</label>
            <input type="text" v-model="descriptorForm.status" placeholder="Enter status (e.g., Low, Medium, High)"
              class="modal-input" />
          </div>

          <div class="form-row">
            <div class="form-group half">
              <label>Min Value (0-100) *</label>
              <input type="number" v-model.number="descriptorForm.min" min="0" max="100" placeholder="0"
                class="modal-input" />
            </div>
            <div class="form-group half">
              <label>Max Value (0-100) *</label>
              <input type="number" v-model.number="descriptorForm.max" min="0" max="100" placeholder="100"
                class="modal-input" />
            </div>
          </div>

          <div class="form-group">
            <label>Description</label>
            <textarea v-model="descriptorForm.description" placeholder="Enter description"
              class="modal-input modal-textarea" rows="3"></textarea>
          </div>
        </div>
        <div class="modal-footer">
          <button class="cancel-btn" @click="closeModal">Cancel</button>
          <button class="save-btn" @click="saveDescriptor" :disabled="!canSave">
            {{ editingDescriptor ? "Update" : "Add" }}
          </button>
        </div>
      </div>
    </div>

    <!-- Delete Confirmation Modal -->
    <div v-if="showDeleteModal" class="modal-overlay" @click="closeDeleteModal">
      <div class="modal-content delete-modal" @click.stop>
        <div class="modal-header">
          <h3>Delete Descriptor</h3>
          <button class="close-btn" @click="closeDeleteModal">
            <i class="bi bi-x"></i>
          </button>
        </div>
        <div class="modal-body">
          <div class="delete-content">
            <div class="delete-icon">
              <i class="bi bi-exclamation-triangle"></i>
            </div>
            <div class="delete-message">
              <h4>Are you sure you want to delete this descriptor?</h4>
              <p>
                <strong>"{{ deletingDescriptor?.status }}" ({{
                  deletingDescriptor?.min
                }}%-{{ deletingDescriptor?.max }}%)</strong>
              </p>
              <p>This action cannot be undone.</p>
            </div>
          </div>
        </div>
        <div class="modal-footer">
          <button class="cancel-btn" @click="closeDeleteModal">Cancel</button>
          <button class="delete-confirm-btn" @click="confirmDeleteDescriptor">
            <i class="bi bi-trash"></i>
            Delete Descriptor
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: "DescriptorSetup",
  props: {
    formData: {
      type: Object,
      default: () => ({
        descriptors: [],
      }),
    },
  },
  emits: ["update-data", "step-completed"],
  data() {
    return {
      selectedItem: null, // Changed from selectedCategory to selectedItem
      expandedNodes: [],
      hierarchySearchQuery: "", // Changed from categorySearchQuery
      descriptorType: "",
      showModal: false,
      showDeleteModal: false,
      showMappingModal: false,
      editingDescriptor: null,
      deletingDescriptor: null,
      mappingDetails: {
        competencyName: "",
        traits: [],
      },

      // Data
      competencies: [],
      traits: [],
      descriptors: [],

      // Form
      descriptorForm: {
        status: "",
        min: 0,
        max: 100,
        description: "",
      },

      // Predefined status mappings
      statusMappings: {
        2: [
          { status: "Low", min: 0, max: 49 },
          { status: "High", min: 50, max: 100 },
        ],
        3: [
          { status: "Low", min: 0, max: 33 },
          { status: "Medium", min: 34, max: 66 },
          { status: "High", min: 67, max: 100 },
        ],
        4: [
          { status: "Low", min: 0, max: 24 },
          { status: "Medium", min: 25, max: 49 },
          { status: "High", min: 50, max: 74 },
          { status: "Excellent", min: 75, max: 100 },
        ],
        5: [
          { status: "Very Low", min: 0, max: 19 },
          { status: "Low", min: 20, max: 39 },
          { status: "Medium", min: 40, max: 59 },
          { status: "High", min: 60, max: 79 },
          { status: "Very High", min: 80, max: 100 },
        ],
      },
    };
  },
  computed: {
    filteredCompetencies() {
      // Only show root competencies (level 1)
      const rootCompetencies = this.competencies.filter(
        (comp) => comp.level === 1
      );

      if (!this.hierarchySearchQuery.trim()) {
        return rootCompetencies;
      }

      const query = this.hierarchySearchQuery.toLowerCase().trim();
      return rootCompetencies.filter((comp) => {
        return this.searchInHierarchy(comp, query);
      });
    },

    displayedDescriptors() {
      if (!this.selectedItem) return [];

      const itemType = this.selectedItem.type;
      const itemId = this.selectedItem.id;

      return this.descriptors.filter(
        (descriptor) =>
          descriptor.itemId === itemId && descriptor.itemType === itemType
      );
    },

    canAddDescriptors() {
      return this.selectedItem && this.selectedItem.descriptorStatus === true;
    },

    canSave() {
      return (
        this.descriptorForm.status &&
        this.descriptorForm.min >= 0 &&
        this.descriptorForm.max <= 100 &&
        this.descriptorForm.min <= this.descriptorForm.max
      );
    },

    allItemsHaveDescriptors() {
      return this.checkAllItemsHaveDescriptors().allComplete;
    },

    incompleteItemsCount() {
      return this.checkAllItemsHaveDescriptors().incompleteItems.length;
    },
  },
  mounted() {
    this.loadData();
    this.autoExpandAndSelect();
    document.addEventListener("keydown", this.handleEscapeKey);
  },
  beforeUnmount() {
    document.removeEventListener("keydown", this.handleEscapeKey);
  },
  methods: {
    // Data loading methods
    loadData() {
      console.log("🔄 Loading descriptor configuration data...");

      try {
        // Load competencies and traits from traitConfiguration
        const traitConfig = localStorage.getItem("traitConfiguration");
        if (traitConfig) {
          const data = JSON.parse(traitConfig);
          this.competencies = data.competencies || [];
          this.traits = data.traits || [];
        }

        // Load existing descriptors
        const descriptorsConfig = localStorage.getItem("descriptorsSetup");
        if (descriptorsConfig) {
          const data = JSON.parse(descriptorsConfig);
          this.descriptors = data.descriptors || [];
        }

        console.log("✅ Loaded descriptor configuration:", {
          competencies: this.competencies.length,
          traits: this.traits.length,
          descriptors: this.descriptors.length,
        });
      } catch (error) {
        console.warn("Error loading descriptor configuration:", error);
      }
    },

    // Hierarchy navigation methods
    searchInHierarchy(competency, query) {
      // Check if current competency matches
      if (competency.name.toLowerCase().includes(query)) return true;

      // Check children competencies
      const children = this.getChildren(competency.id);
      if (children.some((child) => this.searchInHierarchy(child, query)))
        return true;

      // Check traits
      const traits = this.getTraitsForCompetency(competency.id);
      if (traits.some((trait) => trait.name.toLowerCase().includes(query)))
        return true;

      return false;
    },

    hasChildren(competencyId) {
      return this.competencies.some((comp) => comp.parentId === competencyId);
    },

    getChildren(parentId) {
      return this.competencies.filter((comp) => comp.parentId === parentId);
    },

    getChildrenCount(competencyId) {
      return this.getChildren(competencyId).length;
    },

    hasTraits(competencyId) {
      return this.traits.some((trait) => trait.competencyId === competencyId);
    },

    getTraitsForCompetency(competencyId) {
      return this.traits.filter((trait) => trait.competencyId === competencyId);
    },

    getDescriptorCount(itemId, itemType) {
      return this.descriptors.filter(
        (desc) => desc.itemId === itemId && desc.itemType === itemType
      ).length;
    },

    getLinkedTraitsCount(competencyId) {
      return this.getTraitsForCompetency(competencyId).length;
    },

    getDescriptorStatusText(itemId, itemType) {
      const count = this.getDescriptorCount(itemId, itemType);
      if (count === 0) return "Not Configured";
      return "Configured";
    },

    getDescriptorStatusClass(itemId, itemType) {
      const count = this.getDescriptorCount(itemId, itemType);
      return count > 0 ? "status-configured" : "status-empty";
    },

    getDescriptorRowClass(status) {
      const statusLower = status.toLowerCase();
      if (statusLower.includes("low")) return "row-low";
      if (statusLower.includes("medium")) return "row-medium";
      if (statusLower.includes("high")) return "row-high";
      if (statusLower.includes("excellent")) return "row-excellent";
      return "row-default";
    },

    getStatusBadgeClass(status) {
      const statusLower = status.toLowerCase();
      if (statusLower.includes("low")) return "badge-low";
      if (statusLower.includes("medium")) return "badge-medium";
      if (statusLower.includes("high")) return "badge-high";
      if (statusLower.includes("excellent")) return "badge-excellent";
      return "badge-default";
    },

    isExpanded(nodeId) {
      return this.expandedNodes.includes(nodeId);
    },

    toggleExpand(nodeId) {
      const index = this.expandedNodes.indexOf(nodeId);
      if (index > -1) {
        this.expandedNodes.splice(index, 1);
      } else {
        this.expandedNodes.push(nodeId);
      }
    },

    selectItem(item, type) {
      this.selectedItem = {
        ...item,
        type: type,
      };
      this.descriptorType = "";

      const itemTypeText = type === "competency" ? "Competency" : "Trait";
      this.showNotification(
        `Selected ${itemTypeText}: ${item.name}`,
        "success"
      );
    },

    autoExpandAndSelect() {
      // Auto-expand all nodes that have children
      this.competencies.forEach((comp) => {
        if (comp.level === 1 && this.hasChildren(comp.id)) {
          this.expandedNodes.push(comp.id);

          // Expand sub-competencies
          this.getChildren(comp.id).forEach((subComp) => {
            if (this.hasChildren(subComp.id)) {
              this.expandedNodes.push(subComp.id);

              // Expand child competencies that have traits
              this.getChildren(subComp.id).forEach((childComp) => {
                if (this.hasTraits(childComp.id)) {
                  this.expandedNodes.push(`comp-${childComp.id}`);
                }
              });
            }
          });
        }
      });

      // Auto-select first trait if available
      for (const competency of this.competencies) {
        const traits = this.getTraitsForCompetency(competency.id);
        if (traits.length > 0) {
          this.selectedItem = {
            ...traits[0],
            type: "trait",
          };
          console.log("✅ Auto-selected first trait:", traits[0].name);
          break;
        }
      }
    },

    // Mapping methods
    showMappingDetails(competencyId) {
      const competency = this.competencies.find((c) => c.id === competencyId);
      const traits = this.getTraitsForCompetency(competencyId);

      this.mappingDetails = {
        competencyName: competency ? competency.name : "Unknown",
        traits: traits,
      };

      this.showMappingModal = true;
    },

    closeMappingModal() {
      this.showMappingModal = false;
      this.mappingDetails = {
        competencyName: "",
        traits: [],
      };
    },

    // Descriptor type handling
    handleTypeChange() {
      if (this.descriptorType && this.selectedItem) {
        this.generateDescriptorsForType(this.descriptorType);
      }
    },

    setDescriptorType(type) {
      this.descriptorType = type;
      this.generateDescriptorsForType(type);
    },

    generateDescriptorsForType(type) {
      if (
        !this.selectedItem ||
        !this.statusMappings[type] ||
        !this.canAddDescriptors
      )
        return;

      // Clear existing descriptors for this item
      this.descriptors = this.descriptors.filter(
        (desc) =>
          !(
            desc.itemId === this.selectedItem.id &&
            desc.itemType === this.selectedItem.type
          )
      );

      // Generate new descriptors
      const mappings = this.statusMappings[type];
      mappings.forEach((mapping) => {
        const newDescriptor = {
          id: `desc_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
          itemId: this.selectedItem.id,
          itemType: this.selectedItem.type,
          status: mapping.status,
          min: mapping.min,
          max: mapping.max,
          description: "",
          dateCreated: new Date().toISOString(),
        };
        this.descriptors.push(newDescriptor);
      });

      this.showNotification(
        `Generated ${mappings.length} descriptors for ${this.selectedItem.name}`,
        "success"
      );
      this.saveToLocalStorage();
    },

    // CRUD operations
    openAddDescriptorModal() {
      if (!this.canAddDescriptors) {
        this.showNotification(
          "Descriptors are locked for this item",
          "warning"
        );
        return;
      }
      this.editingDescriptor = null;
      this.resetForm();
      this.showModal = true;
    },

    editDescriptor(index) {
      const descriptor = this.displayedDescriptors[index];
      this.editingDescriptor = descriptor;
      this.descriptorForm = {
        status: descriptor.status,
        min: descriptor.min,
        max: descriptor.max,
        description: descriptor.description || "",
      };
      this.showModal = true;
    },

    deleteDescriptor(index) {
      const descriptor = this.displayedDescriptors[index];
      this.deletingDescriptor = descriptor;
      this.showDeleteModal = true;
    },

    confirmDeleteDescriptor() {
      if (!this.deletingDescriptor) return;

      const index = this.descriptors.findIndex(
        (d) => d.id === this.deletingDescriptor.id
      );
      if (index > -1) {
        this.descriptors.splice(index, 1);
        this.showNotification(
          `Descriptor "${this.deletingDescriptor.status}" deleted successfully`,
          "success"
        );
        this.saveToLocalStorage();
      }
      this.closeDeleteModal();
    },

    saveDescriptor() {
      if (!this.canSave || !this.canAddDescriptors) return;

      if (this.editingDescriptor) {
        // Update existing descriptor
        const index = this.descriptors.findIndex(
          (d) => d.id === this.editingDescriptor.id
        );
        if (index > -1) {
          this.descriptors[index] = {
            ...this.editingDescriptor,
            status: this.descriptorForm.status,
            min: this.descriptorForm.min,
            max: this.descriptorForm.max,
            description: this.descriptorForm.description,
            dateModified: new Date().toISOString(),
          };
        }
      } else {
        // Create new descriptor
        const newDescriptor = {
          id: `desc_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
          itemId: this.selectedItem.id,
          itemType: this.selectedItem.type,
          status: this.descriptorForm.status,
          min: this.descriptorForm.min,
          max: this.descriptorForm.max,
          description: this.descriptorForm.description,
          dateCreated: new Date().toISOString(),
        };
        this.descriptors.push(newDescriptor);
      }

      this.showNotification(
        `Descriptor ${this.editingDescriptor ? "updated" : "created"
        } successfully`,
        "success"
      );
      this.closeModal();
      this.saveToLocalStorage();
    },

    // Modal management
    closeModal() {
      this.showModal = false;
      this.editingDescriptor = null;
      this.resetForm();
    },

    closeDeleteModal() {
      this.showDeleteModal = false;
      this.deletingDescriptor = null;
    },

    resetForm() {
      this.descriptorForm = {
        status: "",
        min: 0,
        max: 100,
        description: "",
      };
    },

    handleEscapeKey(event) {
      if (event.key === "Escape") {
        if (this.showModal) {
          this.closeModal();
        } else if (this.showDeleteModal) {
          this.closeDeleteModal();
        } else if (this.showMappingModal) {
          this.closeMappingModal();
        }
      }
    },

    // Configuration completion
    configureDescriptors() {
      const itemsCheck = this.checkAllItemsHaveDescriptors();

      if (!itemsCheck.allComplete) {
        const incompleteList = itemsCheck.incompleteItems
          .map((item) => `${item.name} (${item.type})`)
          .join(", ");

        this.showNotification(
          `Each competency and trait must have at least one descriptor before proceeding.\n\nIncomplete items:\n${incompleteList}`,
          "error"
        );
        return;
      }

      const descriptorData = {
        descriptors: this.descriptors,
      };

      this.$emit("update-data", {
        stepKey: "descriptorsSetup",
        data: descriptorData,
      });

      this.saveToLocalStorage();

      this.showNotification(
        "Descriptor Setup completed! All competencies and traits have performance descriptors.",
        "success"
      );
      this.$emit("step-completed", 6);
    },

    checkAllItemsHaveDescriptors() {
      const incompleteItems = [];

      // Check all competencies that can have descriptors
      for (const competency of this.competencies) {
        if (competency.descriptorStatus === true) {
          const competencyDescriptors = this.descriptors.filter(
            (desc) =>
              desc.itemId === competency.id && desc.itemType === "competency"
          );

          if (competencyDescriptors.length === 0) {
            incompleteItems.push({
              ...competency,
              type: "competency",
            });
          }
        }
      }

      // Check all traits that can have descriptors
      for (const trait of this.traits) {
        if (trait.descriptorStatus === true) {
          const traitDescriptors = this.descriptors.filter(
            (desc) => desc.itemId === trait.id && desc.itemType === "trait"
          );

          if (traitDescriptors.length === 0) {
            incompleteItems.push({
              ...trait,
              type: "trait",
            });
          }
        }
      }

      return {
        allComplete: incompleteItems.length === 0,
        incompleteItems,
      };
    },

    saveToLocalStorage() {
      localStorage.setItem(
        "descriptorsSetup",
        JSON.stringify({
          descriptors: this.descriptors,
        })
      );
    },

    showNotification(message, type = "info") {
      const alertClass =
        {
          success: "alert-success",
          error: "alert-danger",
          info: "alert-info",
          warning: "alert-warning",
        }[type] || "alert-info";

      const notification = document.createElement("div");
      notification.className = `alert ${alertClass} alert-dismissible fade show position-fixed`;
      notification.style.cssText =
        "top: 20px; right: 20px; z-index: 9999; min-width: 300px;";
      notification.innerHTML = `
        ${message}
        <button type="button" class="btn-close" data-bs-dismiss="alert"></button>
      `;

      document.body.appendChild(notification);

      setTimeout(() => {
        if (notification.parentNode) {
          notification.parentNode.removeChild(notification);
        }
      }, 5000);
    },
  },
};
</script>

<style scoped>
/* Base Layout */
.descriptor-configuration {
  min-height: 100vh;
  background-color: #f8f9fa;
  padding: 2rem;
  font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", "Roboto",
    sans-serif;
}

.header-section {
  margin-bottom: 2rem;
}

.page-title {
  font-size: 2rem;
  font-weight: 600;
  color: #1f2937;
  margin-bottom: 0.5rem;
}

.page-subtitle {
  font-size: 1rem;
  color: #6b7280;
  margin: 0;
}

.main-content {
  background: white;
  border-radius: 12px;
  overflow: hidden;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
}

.content-layout {
  display: grid;
  grid-template-columns: 350px 1fr;
  min-height: 70vh;
}

/* Left Panel - Hierarchy Tree */
.left-panel {
  background-color: #fafafa;
  border-right: 1px solid #e0e0e0;
}

.panel-header {
  padding: 1.5rem 1.5rem 1rem;
  border-bottom: 1px solid #e0e0e0;
  background-color: white;
}

.panel-title {
  font-size: 1.125rem;
  font-weight: 600;
  color: #2d3748;
  margin-bottom: 0.5rem;
}

.panel-hint {
  font-size: 0.75rem;
  color: #718096;
  margin-bottom: 1rem;
  font-style: italic;
  background: #e6fffa;
  padding: 0.5rem;
  border-radius: 4px;
  border-left: 3px solid #38b2ac;
}

.search-box {
  position: relative;
}

.search-box i {
  position: absolute;
  left: 0.75rem;
  top: 50%;
  transform: translateY(-50%);
  color: #a0aec0;
  font-size: 0.875rem;
}

.search-box input {
  width: 100%;
  padding: 0.75rem 0.75rem 0.75rem 2.25rem;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  font-size: 0.875rem;
  outline: none;
  background-color: white;
  transition: all 0.2s ease;
}

.search-box input:focus {
  border-color: #8b5cf6;
  box-shadow: 0 0 0 3px rgba(139, 92, 246, 0.1);
}

/* Hierarchy Tree Styles */
.hierarchy-tree {
  padding: 1rem 0;
  max-height: calc(100vh - 200px);
  overflow-y: auto;
}

.tree-node {
  margin-bottom: 0.5rem;
}

/* Root Competency Nodes */
.competency-node {
  display: flex;
  flex-direction: column;
  margin: 0.5rem 1rem;
  cursor: pointer;
  transition: all 0.2s ease;
  border-radius: 8px;
  background-color: white;
  border: 1px solid #e2e8f0;
}

.competency-node.has-children:hover,
.competency-node.selectable:hover {
  background-color: #f0fff4;
  border-color: #48bb78;
  transform: translateX(2px);
  box-shadow: 0 2px 4px rgba(72, 187, 120, 0.1);
}

.competency-node.active {
  background-color: #48bb78;
  color: white;
  box-shadow: 0 4px 12px rgba(72, 187, 120, 0.3);
  border-color: #38a169;
}

.node-content {
  display: flex;
  align-items: center;
  width: 100%;
  padding: 0.75rem 1rem;
}

.competency-meta {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0 1rem 0.75rem 1rem;
  margin-top: -0.25rem;
}

.chevron-icon {
  margin-right: 0.75rem;
  font-size: 0.875rem;
  color: #718096;
  transition: all 0.2s ease;
  width: 16px;
  text-align: center;
  cursor: pointer;
}

.chevron-icon:hover {
  color: #8b5cf6;
}

.competency-type-icon {
  margin-right: 0.5rem;
  font-size: 0.875rem;
  color: #805ad5;
  opacity: 0.8;
}

.competency-name {
  font-weight: 600;
  font-size: 0.95rem;
  flex: 1;
  color: #4a5568;
}

.competency-node.active .competency-name {
  color: white;
}

/* Status Indicators */
.status-indicators {
  margin-left: auto;
  display: flex;
  align-items: center;
  gap: 0.25rem;
}

.status-icon {
  font-size: 0.875rem;
  opacity: 0.7;
}

.status-icon.bi-lock-fill {
  color: #e53e3e;
}

.status-icon.bi-unlock-fill {
  color: #48bb78;
}

.competency-node.active .status-icon {
  opacity: 1;
  color: white;
}

/* Children Containers */
.children-container {
  position: relative;
  margin-left: 1rem;
}

.children-container::before {
  content: "";
  position: absolute;
  left: 25px;
  top: 0;
  bottom: 0;
  width: 1px;
  background: #e2e8f0;
}

.child-wrapper {
  position: relative;
}

/* Sub-Competency Nodes */
.sub-competency-node {
  margin-bottom: 0.75rem;
  cursor: pointer;
  transition: all 0.2s ease;
  border-radius: 8px;
  background-color: white;
  border: 1px solid #e2e8f0;
  margin-right: 16px;
}

.sub-competency-node.selectable:hover {
  background-color: #f0fff4;
  border-color: #48bb78;
  transform: translateX(2px);
}

.sub-competency-node.active {
  background-color: #48bb78;
  color: white;
  box-shadow: 0 4px 12px rgba(72, 187, 120, 0.3);
  border-color: #38a169;
}

.child-content {
  padding: 0.75rem 1rem;
  display: flex;
  flex-direction: column;
}

.node-header {
  display: flex;
  align-items: center;
  width: 100%;
}

.child-meta {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-top: 0.5rem;
  font-size: 0.8rem;
}

.tree-lines {
  position: relative;
  width: 20px;
  height: 20px;
  margin-right: 0.5rem;
}

.vertical-line {
  position: absolute;
  left: 10px;
  top: -10px;
  width: 1px;
  height: 30px;
  background: #e2e8f0;
}

.horizontal-line {
  position: absolute;
  left: 10px;
  top: 10px;
  width: 10px;
  height: 1px;
  background: #e2e8f0;
}

.child-name {
  font-weight: 500;
  font-size: 0.9rem;
  color: #4a5568;
  flex: 1;
}

.sub-competency-node.active .child-name {
  color: white;
}

/* Grandchildren (Child of Sub-Competencies) */
.grandchildren-container {
  position: relative;
  margin-left: 1rem;
}

.grandchildren-container::before {
  content: "";
  position: absolute;
  left: 25px;
  top: 0;
  bottom: 0;
  width: 1px;
  background: #e2e8f0;
}

.grandchild-wrapper {
  position: relative;
}

.child-competency-node {
  margin-bottom: 0.75rem;
  cursor: pointer;
  transition: all 0.2s ease;
  border-radius: 8px;
  background-color: white;
  border: 1px solid #e2e8f0;
  margin-right: 16px;
}

.child-competency-node.selectable:hover {
  background-color: #f0fff4;
  border-color: #48bb78;
  transform: translateX(2px);
}

.child-competency-node.active {
  background-color: #48bb78;
  color: white;
  box-shadow: 0 4px 12px rgba(72, 187, 120, 0.3);
  border-color: #38a169;
}

.grandchild-content {
  padding: 0.75rem 1rem;
  display: flex;
  flex-direction: column;
}

.grandchild-name {
  font-weight: 500;
  font-size: 0.9rem;
  color: #4a5568;
  flex: 1;
}

.child-competency-node.active .grandchild-name {
  color: white;
}

.grandchild-meta {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-top: 0.5rem;
  font-size: 0.8rem;
}

/* Traits Container */
.traits-container {
  position: relative;
  margin-left: 1rem;
}

.traits-container::before {
  content: "";
  position: absolute;
  left: 25px;
  top: 0;
  bottom: 0;
  width: 1px;
  background: #e2e8f0;
}

.trait-wrapper {
  position: relative;
}

.trait-node {
  margin-bottom: 0.75rem;
  cursor: pointer;
  transition: all 0.2s ease;
  border-radius: 8px;
  background-color: white;
  border: 1px solid #e2e8f0;
  margin-right: 16px;
}

.trait-node.selectable:hover {
  background-color: #fff5f5;
  border-color: #f56565;
  transform: translateX(2px);
}

.trait-node.active {
  background-color: #f56565;
  color: white;
  box-shadow: 0 4px 12px rgba(245, 101, 101, 0.3);
  border-color: #e53e3e;
}

.trait-content {
  padding: 0.75rem 1rem;
  display: flex;
  flex-direction: column;
}

.trait-type-icon {
  margin-right: 0.5rem;
  font-size: 0.875rem;
  color: #8b5cf6;
  opacity: 0.8;
}

.trait-name {
  font-weight: 500;
  font-size: 0.9rem;
  color: #4a5568;
  flex: 1;
}

.trait-node.active .trait-name {
  color: white;
}

.trait-meta {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-top: 0.5rem;
  font-size: 0.8rem;
}

/* Count styles */
.items-count,
.descriptor-count {
  font-size: 0.75rem;
  color: #718096;
  background-color: #edf2f7;
  padding: 0.25rem 0.5rem;
  border-radius: 12px;
}

.descriptor-count {
  background-color: #e6fffa;
  color: #22543d;
}

.competency-node.active .items-count,
.competency-node.active .descriptor-count,
.sub-competency-node.active .items-count,
.sub-competency-node.active .descriptor-count,
.child-competency-node.active .items-count,
.child-competency-node.active .descriptor-count,
.trait-node.active .descriptor-count {
  background-color: rgba(255, 255, 255, 0.2);
  color: white;
}

/* Status Pills */
.status-pill {
  padding: 0.125rem 0.375rem;
  border-radius: 8px;
  font-size: 0.7rem;
  font-weight: 600;
  color: white;
  margin-left: 0.5rem;
}

.status-configured {
  background-color: #48bb78;
}

.status-empty {
  background-color: #cbd5e0;
  color: #4a5568;
}

.trait-node.active .status-pill {
  background-color: rgba(255, 255, 255, 0.2);
  color: white;
}

/* Right Panel - Descriptors */
.right-panel {
  background-color: white;
  display: flex;
  flex-direction: column;
}

.empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  text-align: center;
  padding: 4rem 2rem;
  color: #718096;
}

.empty-state .empty-icon {
  font-size: 3rem;
  margin-bottom: 1rem;
  color: #cbd5e0;
}

.empty-state h3 {
  font-size: 1.5rem;
  font-weight: 600;
  color: #2d3748;
  margin-bottom: 0.5rem;
}

.empty-state p {
  font-size: 1rem;
  color: #718096;
}

.selection-hint {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  margin-top: 1rem;
  padding: 0.75rem;
  background: #e6fffa;
  border-radius: 6px;
  color: #22543d;
  font-size: 0.875rem;
}

.selection-hint i {
  color: #38b2ac;
}

.descriptor-section {
  display: flex;
  flex-direction: column;
  height: 100%;
}

.descriptor-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1.5rem 2rem 1rem;
  border-bottom: 1px solid #e2e8f0;
  background-color: #fafafa;
}

.header-left {
  display: flex;
  flex-direction: column;
}

.descriptor-title {
  font-size: 1.25rem;
  font-weight: 600;
  color: #2d3748;
  margin-bottom: 0.25rem;
}

.item-info {
  display: flex;
  align-items: center;
  gap: 1rem;
  margin-top: 0.5rem;
}

.item-type-badge {
  padding: 0.25rem 0.5rem;
  border-radius: 12px;
  font-size: 0.75rem;
  font-weight: 600;
  color: white;
}

.type-competency {
  background-color: #8b5cf6;
}

.type-trait {
  background-color: #f56565;
}

.header-right {
  display: flex;
  align-items: center;
  gap: 1rem;
}

.type-selector {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.type-selector label {
  font-size: 0.875rem;
  font-weight: 500;
  color: #4a5568;
}

.type-select {
  padding: 0.5rem;
  border: 1px solid #e2e8f0;
  border-radius: 6px;
  font-size: 0.875rem;
  outline: none;
  min-width: 200px;
  background: white;
}

.type-select:focus {
  border-color: #8b5cf6;
  box-shadow: 0 0 0 3px rgba(139, 92, 246, 0.1);
}

.add-btn {
  background: linear-gradient(135deg, #8b5cf6 0%, #7c3aed 100%);
  color: white;
  border: none;
  padding: 0.6rem 1.25rem;
  border-radius: 8px;
  font-size: 0.875rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  box-shadow: 0 2px 4px rgba(139, 92, 246, 0.2);
}

.add-btn:hover:not(:disabled) {
  background: linear-gradient(135deg, #7c3aed 0%, #6d28d9 100%);
  transform: translateY(-1px);
  box-shadow: 0 4px 8px rgba(139, 92, 246, 0.3);
}

.add-btn:disabled {
  background: #a0aec0;
  cursor: not-allowed;
  transform: none;
  box-shadow: none;
}

/* Locked Message */
.locked-message {
  margin: 1rem 2rem;
  padding: 1rem;
  background: #fed7d7;
  border: 1px solid #f56565;
  border-radius: 8px;
}

.locked-content {
  display: flex;
  align-items: center;
  gap: 1rem;
}

.locked-content i {
  font-size: 1.5rem;
  color: #e53e3e;
}

.locked-text h4 {
  margin: 0 0 0.25rem 0;
  color: #742a2a;
  font-size: 1rem;
}

.locked-text p {
  margin: 0;
  color: #742a2a;
  font-size: 0.875rem;
}

/* Table Styles */
.table-container {
  flex: 1;
  overflow-x: auto;
  background: white;
  padding: 1rem 2rem;
}

.descriptors-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 0.875rem;
}

.descriptors-table th {
  background: #f8f9fa;
  padding: 1rem;
  text-align: left;
  font-weight: 600;
  color: #4a5568;
  border-bottom: 2px solid #e2e8f0;
  position: sticky;
  top: 0;
  z-index: 10;
}

.descriptors-table td {
  padding: 1rem;
  border-bottom: 1px solid #e2e8f0;
  vertical-align: middle;
}

.descriptor-row {
  transition: all 0.2s ease;
}

.descriptor-row:hover {
  background-color: #f7fafc;
}

/* Row classes based on status */
.row-low {
  background-color: #fed7d7;
}

.row-medium {
  background-color: #fef5e7;
}

.row-high {
  background-color: #c6f6d5;
}

.row-excellent {
  background-color: #bee3f8;
}

.row-default {
  background-color: #f7fafc;
}

.status-col {
  width: 120px;
}

.range-col {
  width: 120px;
}

.description-col {
  min-width: 200px;
}

.mapping-col {
  width: 180px;
}

.actions-col {
  width: 120px;
  text-align: center;
}

.status-badge {
  padding: 0.25rem 0.5rem;
  border-radius: 12px;
  font-size: 0.75rem;
  font-weight: 600;
  color: white;
}

.badge-low {
  background-color: #f56565;
}

.badge-medium {
  background-color: #ed8936;
}

.badge-high {
  background-color: #48bb78;
}

.badge-excellent {
  background-color: #4299e1;
}

.badge-default {
  background-color: #a0aec0;
}

.range-display {
  display: flex;
  align-items: center;
}

.range-text {
  font-weight: 500;
  color: #2d3748;
}

.description-container {
  max-width: 250px;
}

.description-text {
  color: #4a5568;
  line-height: 1.4;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

/* Mapping Styles */
.mapping-container {
  text-align: center;
}

.trait-mapping {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
}

.mapping-info {
  font-size: 0.75rem;
  color: #8b5cf6;
  font-weight: 500;
  display: flex;
  align-items: center;
  gap: 0.25rem;
}

.competency-mapping {
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
}

.mapping-count {
  font-size: 0.75rem;
  color: #48bb78;
  font-weight: 500;
  background-color: #c6f6d5;
  padding: 0.25rem 0.5rem;
  border-radius: 8px;
}

.view-mapping-btn {
  background: #8b5cf6;
  color: white;
  border: none;
  padding: 0.25rem 0.5rem;
  border-radius: 4px;
  font-size: 0.7rem;
  cursor: pointer;
  transition: all 0.2s ease;
}

.view-mapping-btn:hover {
  background: #7c3aed;
}

.actions-container {
  display: flex;
  gap: 0.25rem;
  justify-content: center;
}

.action-btn {
  background: none;
  border: 1px solid #e2e8f0;
  color: #718096;
  padding: 0.375rem;
  border-radius: 4px;
  cursor: pointer;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
}

.edit-btn:hover {
  background: #8b5cf6;
  color: white;
  border-color: #8b5cf6;
}

.delete-btn:hover {
  background: #e53e3e;
  color: white;
  border-color: #e53e3e;
}

.table-empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  text-align: center;
  padding: 4rem 2rem;
  color: #718096;
}

.table-empty .empty-icon {
  font-size: 3rem;
  margin-bottom: 1rem;
  color: #cbd5e0;
}

.table-empty h4 {
  font-size: 1.25rem;
  font-weight: 600;
  color: #2d3748;
  margin-bottom: 0.5rem;
}

.table-empty p {
  font-size: 1rem;
  color: #718096;
  margin-bottom: 1.5rem;
}

.quick-actions {
  display: flex;
  gap: 1rem;
}

.quick-btn {
  background: linear-gradient(135deg, #8b5cf6 0%, #7c3aed 100%);
  color: white;
  border: none;
  padding: 0.5rem 1rem;
  border-radius: 6px;
  font-size: 0.875rem;
  cursor: pointer;
  transition: all 0.2s ease;
  box-shadow: 0 2px 4px rgba(139, 92, 246, 0.2);
}

.quick-btn:hover {
  background: linear-gradient(135deg, #7c3aed 0%, #6d28d9 100%);
  transform: translateY(-1px);
  box-shadow: 0 4px 8px rgba(139, 92, 246, 0.3);
}

/* Configure Section */
.configure-section {
  padding: 1.5rem 2rem;
  background: white;
  border-top: 1px solid #e2e8f0;
}

.configure-btn {
  background: linear-gradient(135deg, #8b5cf6 0%, #7c3aed 100%);
  color: white;
  border: none;
  padding: 0.875rem 2rem;
  border-radius: 8px;
  font-size: 1rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  width: 100%;
  box-shadow: 0 2px 4px rgba(139, 92, 246, 0.2);
}

.configure-btn:hover:not(:disabled) {
  background: linear-gradient(135deg, #7c3aed 0%, #6d28d9 100%);
  transform: translateY(-1px);
  box-shadow: 0 4px 8px rgba(139, 92, 246, 0.3);
}

.configure-btn:disabled {
  background: #a0aec0;
  cursor: not-allowed;
  transform: none;
  box-shadow: none;
}

/* Modal Styles */
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.6);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  backdrop-filter: blur(4px);
  animation: fadeIn 0.2s ease-out;
}

@keyframes fadeIn {
  from {
    opacity: 0;
  }

  to {
    opacity: 1;
  }
}

.modal-content {
  background: white;
  border-radius: 12px;
  width: 90%;
  max-width: 600px;
  max-height: 90vh;
  overflow: hidden;
  box-shadow: 0 20px 40px -10px rgba(0, 0, 0, 0.2);
  display: flex;
  flex-direction: column;
  animation: slideIn 0.3s ease-out;
}

@keyframes slideIn {
  from {
    opacity: 0;
    transform: translateY(-20px) scale(0.95);
  }

  to {
    opacity: 1;
    transform: translateY(0) scale(1);
  }
}

.mapping-modal {
  max-width: 500px;
}

.delete-modal {
  max-width: 400px;
}

.modal-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1.25rem 1.5rem 1rem;
  border-bottom: 1px solid #e2e8f0;
  background: linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%);
  border-radius: 12px 12px 0 0;
}

.modal-header h3 {
  margin: 0;
  font-size: 1.125rem;
  font-weight: 700;
  color: #1e293b;
  background: linear-gradient(135deg, #8b5cf6 0%, #7c3aed 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
}

.close-btn {
  background: none;
  border: none;
  color: #64748b;
  font-size: 1.25rem;
  cursor: pointer;
  padding: 0.375rem;
  border-radius: 6px;
  transition: all 0.2s ease;
}

.close-btn:hover {
  background: #f1f5f9;
  color: #1e293b;
  transform: scale(1.1);
}

.modal-body {
  flex: 1;
  padding: 1.25rem 1.5rem;
  overflow-y: auto;
  background: #fafafa;
}

/* Mapping Details Modal */
.mapping-details h4 {
  margin: 0 0 1rem 0;
  color: #2d3748;
  font-size: 1.125rem;
}

.mapping-description {
  margin: 0 0 1rem 0;
  color: #4a5568;
  font-size: 0.875rem;
}

.traits-list {
  max-height: 300px;
  overflow-y: auto;
}

.trait-item {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 0.75rem;
  background: #f7fafc;
  border: 1px solid #e2e8f0;
  border-radius: 6px;
  margin-bottom: 0.5rem;
  transition: all 0.2s ease;
}

.trait-item:last-child {
  margin-bottom: 0;
}

.trait-item:hover {
  background: #edf2f7;
  border-color: #cbd5e0;
}

.trait-item i {
  color: #8b5cf6;
  font-size: 0.875rem;
}

.trait-item .trait-name {
  flex: 1;
  font-weight: 500;
  color: #2d3748;
}

.trait-item .trait-descriptors {
  font-size: 0.75rem;
  color: #718096;
  background: #e2e8f0;
  padding: 0.25rem 0.5rem;
  border-radius: 8px;
}

/* Form Styles */
.form-group {
  margin-bottom: 1.25rem;
}

.form-group:last-child {
  margin-bottom: 0;
}

.form-row {
  display: flex;
  gap: 1rem;
}

.form-group.half {
  flex: 1;
}

.form-group label {
  display: block;
  margin-bottom: 0.5rem;
  font-weight: 600;
  color: #374151;
  font-size: 0.8rem;
  text-transform: uppercase;
  letter-spacing: 0.03em;
}

.modal-input {
  width: 100%;
  padding: 0.75rem;
  border: 1px solid #e5e7eb;
  border-radius: 6px;
  font-size: 0.8rem;
  outline: none;
  transition: all 0.2s ease;
  background: white;
  font-family: inherit;
}

.modal-input:focus {
  border-color: #8b5cf6;
  box-shadow: 0 0 0 3px rgba(139, 92, 246, 0.1);
}

.modal-input::placeholder {
  color: #9ca3af;
}

.modal-textarea {
  resize: vertical;
  min-height: 80px;
}

/* Modal Footer */
.modal-footer {
  display: flex;
  justify-content: flex-end;
  gap: 0.75rem;
  padding: 1rem 1.5rem 1.5rem;
  border-top: 1px solid #e2e8f0;
  background: white;
  border-radius: 0 0 12px 12px;
}

.cancel-btn {
  background: white;
  border: 1px solid #e5e7eb;
  color: #374151;
  padding: 0.625rem 1.25rem;
  border-radius: 6px;
  font-size: 0.8rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
}

.cancel-btn:hover {
  background: #f9fafb;
  border-color: #d1d5db;
}

.save-btn {
  background: linear-gradient(135deg, #10b981 0%, #059669 100%);
  color: white;
  border: none;
  padding: 0.625rem 1.25rem;
  border-radius: 6px;
  font-size: 0.8rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  box-shadow: 0 2px 4px rgba(16, 185, 129, 0.2);
}

.save-btn:hover:not(:disabled) {
  background: linear-gradient(135deg, #059669 0%, #047857 100%);
}

.save-btn:disabled {
  background: #9ca3af;
  cursor: not-allowed;
}

/* Delete Modal Styles */
.delete-content {
  display: flex;
  align-items: flex-start;
  gap: 1rem;
}

.delete-icon {
  flex-shrink: 0;
  width: 48px;
  height: 48px;
  background: #fed7d7;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #e53e3e;
  font-size: 1.5rem;
}

.delete-message {
  flex: 1;
}

.delete-message h4 {
  margin: 0 0 0.5rem 0;
  font-size: 1.125rem;
  font-weight: 600;
  color: #2d3748;
}

.delete-message p {
  margin: 0 0 0.5rem 0;
  color: #4a5568;
  line-height: 1.5;
}

.delete-confirm-btn {
  background: linear-gradient(135deg, #ef4444 0%, #dc2626 100%);
  color: white;
  border: none;
  padding: 0.625rem 1.25rem;
  border-radius: 6px;
  font-size: 0.8rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  box-shadow: 0 2px 4px rgba(239, 68, 68, 0.2);
}

.delete-confirm-btn:hover {
  background: linear-gradient(135deg, #dc2626 0%, #b91c1c 100%);
}

/* Animation for expanding nodes */
@keyframes expand {
  from {
    opacity: 0;
    transform: translateY(-10px);
  }

  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.children-container,
.grandchildren-container,
.traits-container {
  animation: expand 0.3s ease-out;
}

/* Scrollbar Styling */
.hierarchy-tree::-webkit-scrollbar,
.traits-list::-webkit-scrollbar {
  width: 4px;
}

.hierarchy-tree::-webkit-scrollbar-track,
.traits-list::-webkit-scrollbar-track {
  background: #f1f1f1;
  border-radius: 2px;
}

.hierarchy-tree::-webkit-scrollbar-thumb,
.traits-list::-webkit-scrollbar-thumb {
  background: #8b5cf6;
  border-radius: 2px;
}

.hierarchy-tree::-webkit-scrollbar-thumb:hover,
.traits-list::-webkit-scrollbar-thumb:hover {
  background: #7c3aed;
}

/* Responsive Design */
@media (max-width: 1024px) {
  .content-layout {
    grid-template-columns: 300px 1fr;
  }

  .type-selector {
    align-items: flex-start;
  }

  .type-select {
    min-width: 180px;
  }
}

@media (max-width: 768px) {
  .descriptor-configuration {
    padding: 1rem;
  }

  .content-layout {
    grid-template-columns: 1fr;
    grid-template-rows: auto 1fr;
  }

  .left-panel {
    max-height: 40vh;
    border-right: none;
    border-bottom: 1px solid #e0e0e0;
  }

  .descriptor-header {
    flex-direction: column;
    align-items: flex-start;
    gap: 1rem;
  }

  .header-right {
    width: 100%;
    flex-direction: column;
    align-items: stretch;
  }

  .item-info {
    flex-direction: column;
    align-items: flex-start;
    gap: 0.5rem;
  }

  .type-selector {
    width: 100%;
  }

  .type-select {
    min-width: auto;
    width: 100%;
  }

  .form-row {
    flex-direction: column;
  }

  .modal-content {
    width: 95%;
    max-width: none;
    margin: 1rem;
  }

  .modal-footer {
    flex-direction: column-reverse;
  }

  .modal-footer button {
    width: 100%;
  }

  .locked-content {
    flex-direction: column;
    text-align: center;
  }

  .mapping-container {
    font-size: 0.75rem;
  }

  .competency-mapping {
    flex-direction: row;
    justify-content: center;
    align-items: center;
    gap: 0.5rem;
  }
}

@media (max-width: 480px) {
  .page-title {
    font-size: 1.5rem;
  }

  .panel-header {
    padding: 1rem;
  }

  .descriptor-header {
    padding: 1rem;
  }

  .table-container {
    padding: 0.5rem;
  }

  .descriptors-table {
    font-size: 0.75rem;
  }

  .descriptors-table th,
  .descriptors-table td {
    padding: 0.5rem;
  }

  .configure-section {
    padding: 1rem;
  }

  .modal-header,
  .modal-body {
    padding: 1rem;
  }
}

/* Focus states for accessibility */
.competency-node:focus,
.sub-competency-node:focus,
.child-competency-node:focus,
.trait-node:focus {
  outline: 2px solid #8b5cf6;
  outline-offset: 2px;
}

.modal-input:focus,
.type-select:focus {
  outline: none;
}

.action-btn:focus,
.add-btn:focus {
  outline: 2px solid #8b5cf6;
  outline-offset: 2px;
}

/* High contrast mode support */
@media (prefers-contrast: high) {
  .status-icon.bi-lock-fill {
    color: #c53030;
  }

  .status-icon.bi-unlock-fill {
    color: #2f855a;
  }

  .locked-message {
    border-width: 2px;
  }

  .competency-node,
  .sub-competency-node,
  .child-competency-node,
  .trait-node {
    border-width: 2px;
  }
}

/* Reduced motion support */
@media (prefers-reduced-motion: reduce) {

  *,
  *::before,
  *::after {
    animation-duration: 0.01ms !important;
    animation-iteration-count: 1 !important;
    transition-duration: 0.01ms !important;
  }
}

/* Print styles */
@media print {
  .descriptor-configuration {
    background: white;
    padding: 0;
  }

  .left-panel {
    display: none;
  }

  .content-layout {
    grid-template-columns: 1fr;
  }

  .descriptor-header {
    border-bottom: 2px solid #000;
    background: white;
  }

  .actions-col {
    display: none;
  }

  .modal-overlay {
    display: none;
  }

  .configure-section {
    display: none;
  }
}
</style>
